/**
 * Template utility functions for managing component IDs and cloning
 */

/**
 * Deep clone an object using JSON serialization
 * @param {Object} obj - The object to clone
 * @returns {Object} A deep clone of the object
 */
export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Generates a unique ID in the format {type}-{timestamp}-{randomString}
 * @param {string} type - The type of component (e.g., "row", "column", "text")
 * @returns {string} A unique identifier like "row-1704067200000-abc123def"
 */
export function generateUniqueId(type) {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 11);
  return `${type}-${timestamp}-${randomString}`;
}

/**
 * Recursively regenerates IDs for a component and all its children
 * @param {Object} component - The component object to process
 * @param {string} component.id - The current component ID
 * @param {string} component.type - The component type
 * @param {Object} [component.props] - The component props
 * @param {Array} [component.children] - Optional array of child components
 * @returns {Object} A cloned component with new IDs for itself and all children
 */
export function regenerateComponentIds(component) {
  // Create a deep clone to avoid mutating the original
  const clonedComponent = deepClone(component);

  // Regenerate the ID using the component type
  clonedComponent.id = generateUniqueId(clonedComponent.type);

  // Recursively process children if they exist
  if (Array.isArray(clonedComponent.children)) {
    clonedComponent.children = clonedComponent.children.map((child) =>
      regenerateComponentIds(child)
    );
  }

  return clonedComponent;
}

/**
 * Regenerates all IDs in a template's content
 * @param {Object} templateContent - The full content object from a template
 * @param {Array} templateContent.components - Array of root components
 * @param {Array|null} [templateContent.selectedComponentPath] - The currently selected component path
 * @returns {Object} A cloned content object with all new IDs and cleared selection
 */
export function regenerateTemplateIds(templateContent) {
  // Deep clone the entire content object
  const clonedContent = deepClone(templateContent);

  // Regenerate IDs for all root components
  if (Array.isArray(clonedContent.components)) {
    clonedContent.components = clonedContent.components.map((component) =>
      regenerateComponentIds(component)
    );
  }

  // Clear the selected component path
  clonedContent.selectedComponentPath = null;

  return clonedContent;
}
